import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { PlayListService } from 'services'
import { trackEvent } from 'utils/segmentService'

const UNKNOWN_SOURCE = 'unknown'
const PLAYBACK_ERROR_MODAL = 'playlist_playback_error'
const QUEUE_REPLACEMENT_MODAL = 'playlist_queue_replacement'

const hasNoSongs = (playlist) => {
  if (Array.isArray(playlist?.songs)) {
    return playlist.songs.length === 0
  }

  return Number(playlist?.tracks) === 0
}

const getPlaylistSongs = async (playlist) => {
  if (Array.isArray(playlist?.songs)) {
    return playlist.songs
  }

  const response = await PlayListService.getTracklist(playlist.playlistid)
  return Array.isArray(response?.data?.items) ? response.data.items : []
}

const usePlaylistPlayback = () => {
  const dispatch = useDispatch()
  const hasQueuedItems = useSelector(
    ({ playQueue }) => (playQueue?.items?.length || 0) > 0
  )
  const playbackPendingRef = useRef(false)
  const [pendingRequest, setPendingRequest] = useState(null)
  const [hasPlaybackError, setHasPlaybackError] = useState(false)
  const [isPlaybackPending, setIsPlaybackPending] = useState(false)

  const performPlayback = useCallback(
    async (request) => {
      if (!request || playbackPendingRef.current) {
        return false
      }

      playbackPendingRef.current = true
      setHasPlaybackError(false)
      setIsPlaybackPending(true)

      try {
        const songs = await getPlaylistSongs(request.playlist)

        if (songs.length === 0) {
          setPendingRequest(null)
          return false
        }

        await dispatch(
          PlayQueueThunks.playPlaylist({
            ...request.playlist,
            songs
          })
        )

        trackEvent('Playlist Played', {
          playlist_id: String(request.playlist.playlistid),
          source: request.source
        })
        setPendingRequest(null)
        return true
      } catch {
        setPendingRequest({
          ...request,
          analyticsModalType:
            request.analyticsModalType || PLAYBACK_ERROR_MODAL
        })
        setHasPlaybackError(true)
        return false
      } finally {
        playbackPendingRef.current = false
        setIsPlaybackPending(false)
      }
    },
    [dispatch]
  )

  const requestPlaylistPlayback = useCallback(
    async (playlist, source = UNKNOWN_SOURCE) => {
      if (!playlist?.playlistid || hasNoSongs(playlist)) {
        return false
      }

      const request = { playlist, source }
      setHasPlaybackError(false)

      if (hasQueuedItems) {
        setPendingRequest({
          ...request,
          analyticsModalType: QUEUE_REPLACEMENT_MODAL
        })
        return false
      }

      return performPlayback(request)
    },
    [hasQueuedItems, performPlayback]
  )

  const confirmPlaylistPlayback = useCallback(
    () => performPlayback(pendingRequest),
    [pendingRequest, performPlayback]
  )

  const cancelPlaylistPlayback = useCallback(() => {
    if (playbackPendingRef.current) {
      return
    }

    setPendingRequest(null)
    setHasPlaybackError(false)
  }, [])

  return {
    analyticsModalType:
      pendingRequest?.analyticsModalType || QUEUE_REPLACEMENT_MODAL,
    cancelPlaylistPlayback,
    confirmPlaylistPlayback,
    hasPlaybackError,
    isConfirmationOpen: Boolean(pendingRequest),
    isPlaybackPending,
    pendingPlaylist: pendingRequest?.playlist || null,
    requestPlaylistPlayback
  }
}

export default usePlaylistPlayback
